import * as _ from 'lodash-es';
import { useState, ChangeEvent, useCallback, useEffect } from 'react';

import { kebabCase } from '@/utils';

import { InputRendererProps } from '../inputs';
import { filterFieldsByShape, reOrderDuctField } from '../state/orders';
import { iconFinder, shapeFinder, findFieldsByType } from '../store';
import {
  GO,
  NIDSymbolEntity,
  TKEvent,
  DUCTSHAPES as SP,
  CATALOGUE_CATEGORY as FT,
  CATALOGUE_TYPE,
  ductFieldsOmmitedBy,
  LENGTH_FIELD,
  LENGTH_STR_FIELD,
  OMIT_FROM_SHOW,
} from '../types';
import {
  getDimensionBy,
  setDimensionBy,
  getSystemBy,
} from '../types/dimension-setting';

/**
 * Add Duct Fitting
 * @param selectType
 * @param lazyCloseHandler
 * @returns
 */
export const useDuctModal = (
  /** user selected item type from popup menu */
  selectType: string,
  lazyCloseHandler: (itemType?: string) => void,
) => {
  const lastSystem = getSystemBy(selectType) || '';
  const [currentSystem, setCurrentSystem] = useState<string>(lastSystem); // system belonged
  const [currentType, setCurrentType] = useState(selectType); // symbol type in capitalized

  // get fields first:
  const fieldsByType = findFieldsByType(selectType);
  const [ductFields, setDuctFields] = useState(fieldsByType); // raw fields from config
  const [useShape, setUseShape] = useState(shapeFinder(selectType));

  // == check last used dimension to determine current values:
  // == this will only take effect in modal openning!
  const lastDimension = getDimensionBy(selectType);
  const lastShape = lastDimension.shape || (useShape ? SP.ROUND : '');
  const [shapeType, setShapeType] = useState(lastShape);
  const lastFieldPairs = _.pick(lastDimension, fieldsByType) as GO;
  const [fieldProps, setFieldProps] = useState(lastFieldPairs);

  /**
   * Realtime field values reset when switch `itemType` in modal
   * @date 2025/03/06
   */
  useEffect(() => {
    const lastDimension = getDimensionBy(currentType);
    const { shape, ...values } = lastDimension;
    // if shape cached:
    shape && setShapeType(shape);
    // if values cached:
    values && setFieldProps(values);
  }, [currentType]);

  /**
   * infer fields to show by `shape`
   */
  const ductFieldsFilterBy = useCallback(
    (shape: string) => {
      const filteredFields = filterFieldsByShape(ductFields, shape).filter(
        (f) => !OMIT_FROM_SHOW.includes(f),
      );
      // re-ordered fields
      return reOrderDuctField(filteredFields);
    },
    [ductFields],
  );

  /**
   * TODO: anyway to separate this function out of this hook? - 2023/07/13
   *
   * figure out the properties needed for each field input component
   *
   * @param fieldName each field name
   * @returns normal object
   */
  const inputFieldRenderer = (fieldName: string): InputRendererProps => {
    const kebabField = kebabCase(fieldName);
    const fieldValueChangeHandler = (key: string, value: string) => {
      // console.log({ key });
      const changedProps = { ...fieldProps, [key]: value };
      setFieldProps(changedProps);
    };
    const isLengthField = fieldName === LENGTH_FIELD;
    const isLengthInString = fieldName === LENGTH_STR_FIELD;
    const inputProps: InputRendererProps = {
      ...fieldProps,
      disabled: isLengthInString,
      fieldName, // `DefaultInput` need this
      fieldLabel: isLengthField ? 'LENGTH(in pixels)' : '',
      fieldValue: fieldProps[kebabField], // `DefaultInput` need this
      fieldHandler: fieldValueChangeHandler,
      blurHandler: () => null, // FIXME: placeholder for now - 2023/11/08
    };

    return inputProps;
  };

  const shapeChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
    setShapeType(event.target.value);
  };

  /**
   * figure out the attributes displayed on the right side panel
   * @log no need to have `id` parameter in this function - @2025/08/08
   * @returns `{}`
   */
  const createFeatureEntity = (): NIDSymbolEntity => {
    const icon = iconFinder(currentType);
    // consider shape field
    const fieldsWithShape = useShape
      ? { shape: shapeType, ...fieldProps }
      : fieldProps;
    // ignore unused attributes
    const fieldsOmmited = ductFieldsOmmitedBy(shapeType);
    const pickedProperties: {
      [key: string]: string;
    } = _.omit(fieldsWithShape, fieldsOmmited);

    const detail: NIDSymbolEntity = {
      category: FT.D,
      itemType: CATALOGUE_TYPE.F,
      system: currentSystem,
      entityType: currentType || 'UNDEFINED',
      icon,
      toolType: FT.D,
      // ! add shape for newly created duct fittiong, to display in side panel
      // ! `undefined` means no need to show shape options/select
      // @2025/07/03
      shape: useShape ? shapeType : undefined,
      ...pickedProperties,
    };

    return detail;
  };

  /**
   * doing 2 jobs:
   *
   * 1. notify map to draw new element with attributes object
   * 2. cache input values to reuse
   */
  const applyNewElementHandler = () => {
    const detail = createFeatureEntity();
    // notify map to start drawing ...
    const event = new CustomEvent(TKEvent.NEWELEMENT, { detail });
    document.dispatchEvent(event);

    // cache shape field
    const fieldsWithShape = useShape
      ? { shape: shapeType, ...fieldProps }
      : fieldProps;
    // cache input
    setDimensionBy(currentType, fieldsWithShape);

    // ! save this type to global filter
    // @2025/03/25
    lazyCloseHandler(detail.entityType);
  };

  const ductTypeChangeHandler = (event: ChangeEvent<HTMLSelectElement>) => {
    setCurrentType(event.target.value);
    setDuctFields(findFieldsByType(event.target.value));
    setUseShape(shapeFinder(event.target.value));
  };

  // lazy close, and dispatch cancel tool event...
  const cancelNewElementHandler = () => {
    lazyCloseHandler();
    const evt = new Event(TKEvent.CANCEL_DRAWING_TOOL);
    document.dispatchEvent(evt);
  };

  return {
    currentSystem,
    currentType,
    shapeType,
    useShape,
    ductFields: ductFieldsFilterBy(shapeType),
    fieldProps, // exposed at 2024/02/28
    setCurrentSystem,
    ductTypeChangeHandler,
    inputFieldRenderer,
    applyNewElementHandler,
    shapeChangeHandler,
    cancelNewElementHandler,
  };
};
